<template>
    <div class="dropdown-selector" ref="dropdownRef">
        <div
            class="dropdown-selector__trigger"
            :class="{ 'is-open': isOpen }"
            @click="toggle"
        >
            <span v-if="!withoutPrefix" class="dropdown-selector__prefix">
                {{ prefix }}
            </span>
            <div
                class="dropdown-selector__selected"
                :class="{ 'without-prefix': withoutPrefix }"
            >
                <span>{{ selectedLabel }}</span>
                <Icon icon="ChevronDown" class="dropdown-selector__icon" />
            </div>
        </div>

        <div v-if="isOpen" class="dropdown-selector__menu">
            <ul class="dropdown-selector__options">
                <li
                    v-for="option in options"
                    :key="option.id"
                    @click="selectOption(option)"
                    class="dropdown-selector__option"
                >
                    <span>{{ option.name }}</span>
                    <Icon
                        v-if="option.id === modelValue"
                        icon="Check"
                        class="dropdown-selector__check"
                    />
                </li>
            </ul>
            <div v-if="hasFooter" class="dropdown-selector__footer">
                <slot name="footer" />
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { onClickOutside } from '@vueuse/core';
import Icon from './Icon.vue';

const props = defineProps({
    prefix: String,
    withoutPrefix: {
        type: Boolean,
        default: false
    }, // i know it's stupid but anyway...
    options: {
        type: Array,
        default: () => []
    },
    modelValue: [String, Number],
    hasFooter: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:modelValue']);
const isOpen = ref(false);
const dropdownRef = ref(null);

onClickOutside(dropdownRef, () => (isOpen.value = false));

const toggle = () => {
    isOpen.value = !isOpen.value;
};

const closeOnScroll = () => {
    if (isOpen.value) {
        isOpen.value = false;
    }
};

const selectedLabel = computed(() => {
    const selected = props.options.find((opt) => opt.id === props.modelValue);
    return selected ? selected.name : '';
});

function selectOption(option) {
    emit('update:modelValue', option.id);
    isOpen.value = false;
}

onMounted(() => {
    window.addEventListener('scroll', closeOnScroll, true);
});

onUnmounted(() => {
    window.removeEventListener('scroll', closeOnScroll, true);
});
</script>

<style scoped lang="scss">
@import '../assets/styles/_main.scss';

.dropdown-selector {
    position: relative;
    display: inline-block;

    &__trigger {
        height: 32px;
        display: flex;
        align-items: center;
        border: 1px solid $border;
        border-radius: 8px;
        cursor: pointer;
        background: $card-background;

        &.is-open {
            border: 1px solid $primary-accent;

            .dropdown-selector__icon {
                transform: rotate(180deg);
            }
        }
    }

    &__prefix {
        display: flex;
        align-items: center;
        padding: 0 8px;
        height: 100%;
        border-radius: 7px 0px 0px 7px;
        background: $card-neutral-background;
        color: $text-mid-emphasis;
        @include fz-14;
        font-weight: 500;
    }

    &__selected {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 0 8px;

        &.without-prefix {
            justify-content: space-between;
            width: 100%;
        }

        span {
            color: $text-high-emphasis;
            @include fz-14;
            font-weight: 500;

            max-width: 160px;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            display: inline-block;
        }
    }

    &__icon {
        color: $icons-mid-emphasis;
        min-width: 20px;
        width: 20px;
        height: 20px;
        transform: rotate(0);
        @include transition-default(transform);
    }

    &__menu {
        width: 220px;
        position: absolute;
        z-index: 100;
        top: 100%;
        right: 0;
        margin-top: 4px;
        background: $card-background;
        border: 1px solid $border;
        padding: 8px;
        border-radius: 8px;
        @include shadow-small;
    }

    &__options {
        padding: 0;
        list-style: none;
        margin: 0;
        max-height: 260px;
        overflow-y: auto;
    }

    &__option {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px;
        cursor: pointer;
        color: $text-high-emphasis;
        @include fz-14;
        font-weight: 500;
        border-radius: 8px;
        background: $card-background;
        @include transition-default(background);

        span {
            max-width: 160px;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            display: inline-block;
        }

        &:hover {
            background: $card-neutral-background;
        }
    }

    &__check {
        color: $primary-accent;
        width: 16px;
        height: 16px;
    }

    &__footer {
        margin-top: 4px;
        border-top: 1px solid $border;
    }
}

::-webkit-scrollbar {
    width: 4px;
    height: 4px;
}

/* Track */
::-webkit-scrollbar-track {
    border-radius: 25px;
}

/* Handle */
::-webkit-scrollbar-thumb {
    background: #b5b5b5;
    border-radius: 4px;
}

body[theme='dark'] .dropdown-selector {
    &__trigger {
        background: $background-secondary;
        border: 1px solid $border-secondary;

        &.is-open {
            border: 1px solid $primary-accent;
        }
    }

    &__prefix {
        background: $card-neutral-background;
        color: $text-mid-emphasis;
    }

    &__selected {
        span {
            color: $text-high-emphasis;
        }
    }

    &__icon {
        color: $icons-mid-emphasis;
    }

    &__menu {
        background: $card-neutral-background;
        border: 1px solid $border;
    }

    &__option {
        color: $text-high-emphasis;
        background: $card-neutral-background;

        &:hover {
            background: $card-background;
        }
    }

    &__check {
        color: $primary-accent;
    }

    &__footer {
        border-top: 1px solid $border;
    }
}
</style>
